import dayjs from 'dayjs';
import type { ApprovalAttributes } from '@/types/approval';
import { localize } from '@/utils/approval/localize';
import { VEHICLE_COST_HR_MISSING_MESSAGE } from '@/utils/approval/vehicleCost';
import { formatAsDate } from '@/utils/format';

/** F&CO 장기근속 포상휴가 — 레거시 appr_edit_forms_hr_longservice_leave(214 와 공유) */
export const FNCO_LONG_SERVICE_LEAVE_TYPE = 240;

export const isLongServiceLeaveType = (apvType: number): boolean =>
  apvType === FNCO_LONG_SERVICE_LEAVE_TYPE;

/** 언어와 무관하게 한국어 문구가 곧 저장값(data.service_years) */
export const FIRST_ANNIVERSARY = '1주년';
export const DEFAULT_SERVICE_YEARS = '3주년';
export const SERVICE_YEARS_OPTIONS = [
  FIRST_ANNIVERSARY,
  '3주년',
  '5주년',
  '7주년',
  '10주년',
  '15주년',
  '20주년',
  '25주년',
  '30주년',
  '35주년',
  '40주년',
] as const;

/** [1주년] 은 사용 기간이 없는 포상 */
export const isFirstAnniversary = (serviceYears: string): boolean =>
  serviceYears === FIRST_ANNIVERSARY;

export const LONG_SERVICE_PLEDGE_ALERT =
  '서약서에 동의해야 품의 상신이 가능합니다.';

const COUNT_MISSING_MESSAGE =
  '사용일수를 계산하지 못했습니다. 기간을 다시 선택해 주세요.';

export type LongServiceApplicant = {
  name: string;
  employeeNumber: string;
  /** 법인·사업부·부서를 공백으로 이은 값 */
  affiliation: string;
  /** [1주년] 제목 괄호 안 값 */
  departmentName: string;
  hireDate: string;
};

export type LongServicePledge = 'Y' | 'N' | '';

export type LongServiceLeaveFormState = {
  applicant: LongServiceApplicant;
  serviceYears: string;
  startsAt: string;
  endsAt: string;
  /** draft-source 계산값. 계산 전·실패면 '' */
  useCount: string;
  pledgeAgree: LongServicePledge;
  /** 저장하려다 막힌 뒤 서약 안내 문구를 띄움 */
  pledgeAlert: boolean;
  /** [1주년] 으로 바꾸기 직전 기간 — 다른 구분으로 돌아오면 복원 */
  periodBeforeFirstAnniversary: { startsAt: string; endsAt: string } | null;
};

/** 레거시 CL02 JSON 키 그대로. [1주년] 은 기간·일수가 빈 문자열 */
export type LongServiceLeaveData = {
  service_years: string;
  start_date: string;
  end_date: string;
  use_count: string;
  pledge_agree: string;
};

type EmployeeSource = {
  name?: string | null;
  employeeNumber?: string | null;
  companyName?: string | null;
  divisionName?: string | null;
  departmentName?: string | null;
  joinedAt?: string | null;
};

export const toLongServiceApplicant = (
  info: EmployeeSource | null | undefined,
): LongServiceApplicant | null => {
  if (!info?.employeeNumber) return null;
  return {
    name: info.name ?? '',
    employeeNumber: info.employeeNumber,
    affiliation: [info.companyName, info.divisionName, info.departmentName]
      .filter(Boolean)
      .join(' '),
    departmentName: info.departmentName ?? '',
    hireDate: info.joinedAt ? formatAsDate(info.joinedAt) : '',
  };
};

export const createLongServiceLeaveState = (
  applicant: LongServiceApplicant,
): LongServiceLeaveFormState => {
  const today = formatAsDate(dayjs());
  return {
    applicant,
    serviceYears: DEFAULT_SERVICE_YEARS,
    startsAt: today,
    endsAt: today,
    useCount: '',
    pledgeAgree: '',
    pledgeAlert: false,
    periodBeforeFirstAnniversary: null,
  };
};

/** 내 인사 정보로 새 작성 상태. 인사 정보(사번)가 없으면 null */
export const longServiceLeaveStateFromMyInfo = (
  info: EmployeeSource | null | undefined,
): LongServiceLeaveFormState | null => {
  const applicant = toLongServiceApplicant(info);
  return applicant ? createLongServiceLeaveState(applicant) : null;
};

/** 레거시 buildSubject — [1주년] 은 문구가 다르고 괄호 앞 공백이 없음 */
export const buildLongServiceLeaveSubject = (
  serviceYears: string,
  applicant: Pick<LongServiceApplicant, 'name' | 'departmentName'>,
): string =>
  isFirstAnniversary(serviceYears)
    ? localize('1주년 장기근속 신청서({{department}}-{{name}})', {
        department: applicant.departmentName,
        name: applicant.name,
      })
    : localize('{{years}} 장기근속 포상휴가 신청서 ({{name}})', {
        years: localize(serviceYears),
        name: applicant.name,
      });

/** 레거시 applyServiceYearsRules — [1주년] 은 기간을 오늘로 눌러 두고, 돌아오면 직전 기간 복원 */
export const changeServiceYears = (
  state: LongServiceLeaveFormState,
  serviceYears: string,
): LongServiceLeaveFormState => {
  const wasFirst = isFirstAnniversary(state.serviceYears);
  const toFirst = isFirstAnniversary(serviceYears);
  if (toFirst && !wasFirst) {
    const today = formatAsDate(dayjs());
    return {
      ...state,
      serviceYears,
      periodBeforeFirstAnniversary: {
        startsAt: state.startsAt,
        endsAt: state.endsAt,
      },
      startsAt: today,
      endsAt: today,
      useCount: '',
    };
  }
  if (!toFirst && wasFirst && state.periodBeforeFirstAnniversary) {
    return {
      ...state,
      serviceYears,
      ...state.periodBeforeFirstAnniversary,
      periodBeforeFirstAnniversary: null,
      useCount: '',
    };
  }
  return { ...state, serviceYears };
};

/** 레거시 calDateRange — 종료일이 시작일보다 앞서면 시작일로 맞춤 */
export const changeStartsAt = (
  state: LongServiceLeaveFormState,
  startsAt: string,
): LongServiceLeaveFormState => ({
  ...state,
  startsAt,
  endsAt: startsAt && state.endsAt < startsAt ? startsAt : state.endsAt,
  useCount: '',
});

export const changeEndsAt = (
  state: LongServiceLeaveFormState,
  endsAt: string,
): LongServiceLeaveFormState => ({
  ...state,
  endsAt: endsAt && endsAt < state.startsAt ? state.startsAt : endsAt,
  useCount: '',
});

/** 이탈 경고 판정용 — 사용자가 고칠 수 있는 값만 */
export const longServiceLeaveKey = (
  state: LongServiceLeaveFormState | null,
): string => {
  if (!state) return '';
  return JSON.stringify([
    state.serviceYears,
    state.startsAt,
    state.endsAt,
    state.pledgeAgree,
  ]);
};

const isDate = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v);

/** 레거시 save_doc_long_service_award 검사(임시저장도 같음) */
export const checkLongServiceLeaveBeforeSave = (
  state: LongServiceLeaveFormState | null | undefined,
): string | null => {
  if (!state) return VEHICLE_COST_HR_MISSING_MESSAGE;
  if (state.pledgeAgree !== 'Y') return LONG_SERVICE_PLEDGE_ALERT;
  if (isFirstAnniversary(state.serviceYears)) return null;
  if (!isDate(state.startsAt) || !isDate(state.endsAt) || !state.useCount) {
    return COUNT_MISSING_MESSAGE;
  }
  return null;
};

export const isPledgeMissing = (message: string | null): boolean =>
  message === LONG_SERVICE_PLEDGE_ALERT;

export const toLongServiceLeaveData = (
  state: LongServiceLeaveFormState,
): LongServiceLeaveData => {
  const first = isFirstAnniversary(state.serviceYears);
  return {
    service_years: state.serviceYears,
    start_date: first ? '' : state.startsAt,
    end_date: first ? '' : state.endsAt,
    use_count: first ? '' : state.useCount,
    pledge_agree: state.pledgeAgree,
  };
};

/** [1주년] 은 서버가 기간을 신청일로 채우므로 startsAt·endsAt 을 보내지 않음 */
export const buildLongServiceLeaveAttributes = (
  state: LongServiceLeaveFormState | null | undefined,
  content: string,
): ApprovalAttributes | null => {
  if (!state) return null;
  const data = toLongServiceLeaveData(state);
  return isFirstAnniversary(state.serviceYears)
    ? { content, data }
    : { content, startsAt: state.startsAt, endsAt: state.endsAt, data };
};

const str = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';

/** 단건조회 attributes.data. 레거시 문서는 JSON 문자열일 수 있음. 포상 내용이 없으면 null */
export const parseLongServiceLeaveData = (
  attributes: unknown,
): LongServiceLeaveData | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  let raw = (attributes as Record<string, unknown>).data;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  if (!str(d.service_years)) return null;
  return {
    service_years: str(d.service_years),
    start_date: str(d.start_date),
    end_date: str(d.end_date),
    use_count: str(d.use_count),
    pledge_agree: str(d.pledge_agree),
  };
};

export const parseLongServiceLeaveContent = (
  attributes: unknown,
): string | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const raw = (attributes as Record<string, unknown>).content;
  return typeof raw === 'string' && raw.trim() ? raw : null;
};

/** 편집 복원 — 신청자는 내 인사 정보(편집은 작성자 본인만), 사용일수는 다시 계산 */
export const longServiceLeaveDataToState = (
  applicant: LongServiceApplicant,
  data: LongServiceLeaveData,
): LongServiceLeaveFormState => {
  const base = createLongServiceLeaveState(applicant);
  const serviceYears = (SERVICE_YEARS_OPTIONS as readonly string[]).includes(
    data.service_years,
  )
    ? data.service_years
    : DEFAULT_SERVICE_YEARS;
  const hasPeriod = isDate(data.start_date) && isDate(data.end_date);
  return {
    ...base,
    serviceYears,
    startsAt: hasPeriod ? data.start_date : base.startsAt,
    endsAt: hasPeriod ? data.end_date : base.endsAt,
    pledgeAgree:
      data.pledge_agree === 'Y' || data.pledge_agree === 'N'
        ? data.pledge_agree
        : '',
  };
};

export type LongServiceLeaveDraftSource = {
  display?: { useCount?: number | null } | null;
};
